import { useEffect, useState } from "react";
import { device } from "web_myplann/Utils/device";
import type { DashboardType } from "../Components/DashboardTitleCard";

export type AnimationDirection = "left" | "right" | null;

export const useDashboardAnimation = (
	dashboards: DashboardType[],
	defaultValue: number,
	enabled = true,
	isCardVisible = true,
) => {
	const [isAnimating, setIsAnimating] = useState(false);
	const [animationDirection, setAnimationDirection] =
		useState<AnimationDirection>(null);
	const [dashboard, setDashboard] = useState<DashboardType>(
		dashboards[defaultValue],
	);
	const [previousDashboard, setPreviousDashboard] =
		useState<DashboardType | null>(null);

	useEffect(() => {
		if (!enabled) return;
		const initial = dashboards[defaultValue];
		if (initial && initial.id !== dashboard?.id) {
			setDashboard(initial);
		}
	}, [defaultValue, enabled]);

	useEffect(() => {
		return () => {
			device.changeDashboardColor("");
		};
	}, []);

	const latestDashboard =
		dashboards.find((d) => d.id === dashboard?.id) ?? dashboards[defaultValue];
	if (latestDashboard && latestDashboard !== dashboard) {
		setDashboard(latestDashboard);
	}

	const activeDashboard = latestDashboard ?? dashboard;

	useEffect(() => {
		if (!enabled || !activeDashboard || !isCardVisible) {
			device.changeDashboardColor("");
			return;
		}
		device.changeDashboardColor(activeDashboard.gradientFrom);
	}, [enabled, activeDashboard?.gradientFrom, isCardVisible]);

	useEffect(() => {
		if (!enabled || !activeDashboard || !isCardVisible) return;

		const restoreColor = () => {
			if (document.visibilityState === "visible") {
				device.changeDashboardColor(activeDashboard.gradientFrom);
			}
		};

		document.addEventListener("visibilitychange", restoreColor);
		window.addEventListener("pageshow", restoreColor);

		return () => {
			document.removeEventListener("visibilitychange", restoreColor);
			window.removeEventListener("pageshow", restoreColor);
		};
	}, [enabled, activeDashboard?.gradientFrom, isCardVisible]);

	const handleDashboardChange = (
		newIndex: number,
		direction: AnimationDirection,
	) => {
		if (isAnimating) return;

		const newDashboard = dashboards[newIndex];
		if (newDashboard.id === dashboard.id) return;

		setPreviousDashboard(dashboard);
		setAnimationDirection(direction);
		setIsAnimating(true);
		setDashboard(newDashboard);

		const timeout = setTimeout(() => {
			setIsAnimating(false);
			setPreviousDashboard(null);
			setAnimationDirection(null);
		}, 300);

		return () => clearTimeout(timeout);
	};

	const calculateDirection = (
		currentDashboard: DashboardType,
		targetIndex: number,
	): AnimationDirection => {
		const currentIndex = dashboards.findIndex(
			(d) => d.id === currentDashboard.id,
		);
		return targetIndex > currentIndex ? "right" : "left";
	};

	return {
		dashboard: latestDashboard ?? dashboard,
		isAnimating,
		previousDashboard,
		animationDirection,
		handleDashboardChange,
		calculateDirection,
	};
};
